import { modals } from '../../providers/modals';
import { useModal } from '../../providers/ModalProvider';
import styles from './CreateCPAModal.module.scss';
import Modal from '../../components/modals/Modal';
import { Text } from '../../components/typography';
import UserSelector from '../../components/UserSelector';
import { loader } from 'graphql.macro';
import { useMutation, useQuery } from '@apollo/client';
import RiskMatrix from '../../components/RiskMatrix';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys';
import { useMemo, useState } from 'react';
import QuickFileDropzone from '../../components/image_uploads/QuickFileDropzone';
import { Spinner } from 'react-bootstrap';
import {
  calculateRiskString,
  cpaStatusTypes,
  correctiveActionTypes,
  correctiveActionTimelines,
  getDueDate,
  auditTypes,
} from '../../utilities/audits';
import { showToast } from '../../graphql/cache/modal';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../constants/strings';
import { useCurrentUser } from '../../providers/UserProvider';
import { format } from 'date-fns';
import UserMultiSelector from '../../components/UserMultiSelector';
import { Button } from '../../components';
import { DropdownText } from '../../components/dropdowns/DropdownText';
import taskImage from '../../assets/clipboard_task.png';
import Select from 'react-select';
import { calculateRisk } from '../../utilities/riskMatrix';
import Label from '../../components/action_items/Label';
import classNames from 'classnames';
import { getLabel } from '../../utilities/incidents';

const addAttachmentMutation = loader('./CreateCPAModal.attachment.graphql');
const addCorrectiveActionMutation = loader('./CreateCPAModal.create.graphql');
const addCPAParticipantMutation = loader('./CPAPage.user.graphql');
const userWorkspaceQuery = loader('./CreateCPAModal.workspaces.graphql');
const existingCPAsQuery = loader('./CreateCPAModal.repeatFetch.graphql');

const CPASearcher = ({ setParentId, parentId }) => {
  const defaultSearchState = {
    string: null,
    results: null,
    searching: false,
  };
  const [searchProps, setSearchProps] = useState(defaultSearchState);

  useQuery(existingCPAsQuery, {
    skip: !searchProps.searching,
    variables: {
      externalId: `%${searchProps?.string}%`,
    },
    onCompleted: (res) => {
      const { correctiveActions = [] } = res;
      const allResults = correctiveActions
        ?.map((item) => {
          const riskCalc = item.impact
            ? item.impact * item.probability
            : item.__typename === 'CorrectiveAction'
            ? item.riskLevel
            : null;

          const risk = calculateRisk(riskCalc);
          return {
            ...item,
            title: item?.externalId,
            risk: risk,
          };
        })
        ?.sort((a, b) => a?.externalId?.localeCompare(b?.externalId));

      setSearchProps((prev) => ({
        ...prev,
        results: allResults,
        searching: false,
      }));
    },
  });

  return (
    <div>
      <Text marginXs weight="semiBold">
        Identify the original finding
      </Text>
      <div className={styles.linkButtonRow}>
        <Form.Control
          placeholder="Search by ID"
          value={searchProps?.string || ''}
          onChange={(e) =>
            setSearchProps((prev) => ({ ...prev, string: e.target.value }))
          }
          onBlur={() =>
            setSearchProps((prev) => ({ ...prev, searching: true }))
          }
        />
        <Button value="Search" icon="search" onClick={() => {}} size="sm" />
        <Button
          value="Clear"
          onClick={() => {
            setSearchProps(defaultSearchState);
            setParentId(null);
          }}
          outlined
          size="sm"
        />
      </div>
      <div className={styles.linkList}>
        {!!searchProps?.results ? (
          searchProps?.results?.length > 0 ? (
            searchProps?.results?.map(({ id, externalId, status, risk }, i) => (
              <div
                className={classNames(
                  parentId === id ? styles.linkCardSelected : {},
                  styles.linkCard,
                  i % 2 !== 0 ? styles.linkOdd : {},
                )}
                onClick={() => setParentId(parentId === id ? null : id)}
              >
                <Text noMargin weight="semiBold">
                  {externalId}
                </Text>

                <div className={styles.linkButtonRow}>
                  <Label
                    className={styles.label}
                    name={getLabel(status)}
                    color={cpaStatusTypes[status]?.color}
                  />
                  <Label name={risk?.level} color={risk?.textColor} rowSized />
                </div>
              </div>
            ))
          ) : (
            <Text>None</Text>
          )
        ) : null}
      </div>
    </div>
  );
};

export default function CreateCPAModal() {
  const { modalState, closeModal, updateModal, openConfirmationModal } =
    useModal();
  const [addAttachment] = useMutation(addAttachmentMutation);
  const { t } = useTranslation();
  const [loadingAttachment, setLoadingAttachment] = useState(false);
  const [addCorrectiveAction] = useMutation(addCorrectiveActionMutation);
  const [addParticipant] = useMutation(addCPAParticipantMutation);
  const { user } = useCurrentUser();
  const navigate = useNavigate();

  const {
    show,
    isObservation,
    probability,
    impact,
    narrative,
    isRepeat,
    attachments = [],
    assignee,
    riskLevel,
    title,
    questionId,
    inspectionId,
    onSubmit,
    parentId,
    navigation = false,
    participants = [],
    type = 'Internal',
    workspace,
    fromEffectivenessReview,
  } = modalState.CPAModal;

  const [rootCause, setRootCause] = useState(false);

  const onHide = () => {
    closeModal({ modalName: modals.CPA });
    setRootCause(false);
  };

  const update = (variables) => {
    updateModal({
      modalName: modals.CPA,
      variables: { ...variables },
    });
  };

  const submitDisabled = !isObservation
    ? !impact || !probability || !assignee || !narrative
    : !assignee || !narrative;

  const { data: { workspacePermissions = [] } = {} } = useQuery(
    userWorkspaceQuery,
    {
      skip: !assignee?.id,
      variables: assignee?.id ? { userId: `${assignee.id}` } : undefined,
    },
  );

  const workspaces = useMemo(() => {
    return (
      workspacePermissions?.map(({ workspace }) => ({
        label: workspace.title,
        value: workspace.id,
        ...workspace,
      })) || []
    );
  }, [workspacePermissions]);

  return (
    <Modal
      disableCloseOnSubmit
      open={show || false}
      titleImage={
        <img className={styles.taskImage} src={taskImage} alt="task_png" />
      }
      onClose={onHide}
      title={`Create ${
        isObservation ? 'Opportunity for Improvement' : 'Finding '
      }`}
      submitDisabled={submitDisabled}
      onSubmit={() => {
        openConfirmationModal({
          title: `Submit ${
            isObservation ? 'Opportunity for Improvement' : 'Finding'
          } `,
          description: `Are you sure you want to submit this ${
            isObservation ? 'OFI' : 'Finding'
          }? This will assign action items and cannot be edited once submitted.`,
          variant: 'success',
          onSubmit: async () => {
            const interimDays = correctiveActionTimelines(
              correctiveActionTypes.interim,
              riskLevel === 'OBSERVATION' ? 'LOW' : riskLevel,
            );
            const finalDays = correctiveActionTimelines(
              correctiveActionTypes.final,
              riskLevel === 'OBSERVATION' ? 'LOW' : riskLevel,
            );
            const correctiveAction = modalState.CPAModal;
            async function handleCorrectiveAction() {
              try {
                const {
                  data: {
                    addCorrectiveAction: { id: correctiveActionId },
                  },
                } = await addCorrectiveAction({
                  variables: {
                    ...correctiveAction,
                    assigneeId: assignee?.id,
                    interimDueDate: getDueDate(interimDays),
                    finalDueDate: getDueDate(finalDays),
                    status: cpaStatusTypes.INTERIM.key,
                    type,
                    inspectionId,
                    questionId,
                    parentId,
                    priority: riskLevel === 'OBSERVATION' ? 'LOW' : riskLevel,
                    participants,
                    rootCauseRequired:
                      riskLevel === 'HIGH' || !!parentId || !!rootCause,
                    isRepeat,
                  },
                });

                await Promise.all(
                  attachments?.map(({ url, name, extension, size }) =>
                    addAttachment({
                      variables: {
                        correctiveActionId,
                        url,
                        name,
                        extension,
                        size,
                      },
                    }),
                  ),
                );

                await Promise.all(
                  participants?.map(({ id: participantId }) =>
                    addParticipant({
                      variables: {
                        userId: participantId,
                        action: 'PARTICIPANT',
                        correctiveActionId,
                      },
                    }),
                  ),
                );

                onHide();
                onSubmit({
                  failNotes: narrative,
                  userId: assignee?.id,
                });

                if (navigation) {
                  navigate(`/${paths.cpa}/${correctiveActionId}`);
                }
                if (correctiveActionId) {
                  showToast({
                    title: 'Success!',
                    message: 'Corrective Preventative Actions Assigned!',
                  });
                }
              } catch (error) {
                console.error('Error handling corrective action:', error);
                onHide();
                showToast({
                  variant: 'warning',
                  title: 'Error',
                  message:
                    error.message || 'Failed to create corrective action.',
                });
              }
            }

            handleCorrectiveAction();
          },
        });
      }}
    >
      <div className={styles.container}>
        <div className={styles.topButtons}>
          <Button
            value={'Finding'}
            className={styles.button}
            icon={!isObservation ? 'check' : 'cancel'}
            variant={isObservation ? 'secondary' : 'success'}
            onClick={() =>
              update({
                isObservation: false,
                riskLevel: null,
                impact: null,
                probability: null,
              })
            }
          />
          <Button
            value={'Improvement'}
            icon={isObservation ? 'check' : 'cancel'}
            variant={!isObservation ? 'secondary' : 'success'}
            className={styles.button}
            onClick={() =>
              update({
                isObservation: true,
                riskLevel: 'OBSERVATION',
                impact: null,
                probability: null,
              })
            }
          />
        </div>
        {!!questionId ? (
          <div className={styles.question}>
            <Text color="red" weight="bold" noMargin>
              Question
            </Text>
            <Text size="md" noMargin>
              {title}
            </Text>
            <div className={styles.spacer} />
          </div>
        ) : (
          <div className={styles.matrix}>
            <Text weight="semiBold" noMargin>
              Type <span className={styles.red}>*</span>
            </Text>
            <DropdownText
              required
              items={auditTypes}
              noMargin
              selected={type}
              placeholder={'Type'}
              onChange={(value) => update({ type: value })}
              onRemove={() => update({ type: null })}
            />
          </div>
        )}
        <Text weight="semiBold" noMargin>
          Assignee <span className={styles.red}>*</span>
        </Text>
        <UserSelector
          type="assignee"
          userQuery="all"
          selected={assignee}
          onSelectionChanged={(assignee) => {
            update({ assignee });
          }}
          onClose={() => {
            update({ assignee: null });
          }}
        />
        <Text weight="semiBold" noMargin>
          Workspace <span className={styles.red}>*</span>
        </Text>
        <Select
          options={workspacePermissions?.map(({ workspace }) => ({
            label: workspace?.title,
            value: workspace?.id,
            ...workspace,
          }))}
          value={workspace || workspaces[0]}
          onChange={(workspace) => {
            update({ workspace });
          }}
        />

        <Text noMargin weight="semiBold">
          Participants
        </Text>
        <UserMultiSelector
          stateUpdateOnly
          setUsers={(newUsers) => update({ participants: newUsers })}
          onUserAdded={(user) => {
            update({ participants: [...participants, user] });
          }}
          className={styles.team}
          type="all"
          selected={participants}
          onUserRemoved={(user) => {
            update({
              participants: participants?.filter((u) => u.id !== user.id),
            });
          }}
          usersToLeaveOut={[user]}
        />
        <Text weight="semiBold" noMargin>
          Narrative <span className={styles.red}>*</span>
        </Text>
        <div>
          <Form.Control
            as="textarea"
            rows={5}
            value={narrative}
            onChange={(e) => update({ narrative: e.target.value })}
            onBlur={() => update({ narrative })}
          />
        </div>
        {!fromEffectivenessReview && (
          <div>
            <div className={styles.check}>
              <Text noMargin weight="semiBold">
                Is this a Repeat Finding?
              </Text>
              <Form.Check
                label="Yes"
                name="isRepeat"
                type={'radio'}
                checked={isRepeat}
                onChange={() => update({ isRepeat: true })}
              />
              <Form.Check
                label="No"
                name="isRepeat"
                type={'radio'}
                checked={!isRepeat}
                onChange={() => update({ isRepeat: false })}
              />
            </div>
            <div>
              {isRepeat && (
                <CPASearcher
                  setParentId={(parentId) => update({ parentId })}
                  parentId={parentId}
                />
              )}
            </div>
          </div>
        )}

        <div className={styles.attachmentContainer}>
          {loadingAttachment ? (
            <div className={styles.spinner}>
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div>
              {!attachments?.length ? (
                <Text weight="semiBold">{t(keys.common.ATTACHMENTS)}</Text>
              ) : null}
              <QuickFileDropzone
                setLoading={setLoadingAttachment}
                icon={attachments?.length > 0}
                iconText={t(keys.common.ATTACHMENTS)}
                className={styles.attachmentText}
                onDelete={(id) => {
                  const updatedAttachments = attachments.filter(
                    (a) => a.id !== id,
                  );
                  update({ attachments: updatedAttachments });
                }}
                readOnly={false}
                attachments={attachments || []}
                loading={loadingAttachment}
                onSubmit={({ url, name, extension, size }) => {
                  const attachment = {
                    url,
                    name,
                    extension,
                    size,
                    creator: user,
                    dateCreated: format(new Date(), 'yyyy-MM-dd'),
                  };
                  update({ attachments: [...attachments, attachment] });
                }}
              />
            </div>
          )}
        </div>
        {!isObservation ? (
          <div className={styles.matrix}>
            <Text weight="semiBold" noMargin>
              Threat Level <span className={styles.red}>*</span>
            </Text>
            <RiskMatrix
              showMessage={true}
              impact={impact}
              probability={probability}
              onChange={(impact, probability) => {
                const risk = calculateRiskString(impact * probability);
                update({ impact, probability, riskLevel: risk });
              }}
            />
          </div>
        ) : null}
      </div>
      {riskLevel !== 'HIGH' && !parentId && (
        <div className={styles.check}>
          <Text noMargin weight="semiBold">
            Root Cause Analysis Required?
          </Text>
          <Form.Check
            label="Yes"
            name="approval"
            type={'radio'}
            checked={rootCause}
            onChange={() => {
              setRootCause(true);
            }}
          />
          <Form.Check
            label="No"
            name="approval"
            type={'radio'}
            checked={!rootCause}
            onChange={() => {
              setRootCause(false);
            }}
          />
        </div>
      )}
    </Modal>
  );
}
